/**
 * Phase 41 Plan 41-09 Task 1 — Surface 6 (full Notifications page).
 *
 * Reuses the Phase 39 backend (notifications stream + bulk mark-read) via the
 * same `useNotifications` / `useMarkNotificationRead` / `useMarkAllNotificationsRead`
 * hooks that BellDropdown consumes (plan 41-03). Mark-all-read on this page
 * invalidates the same `['notifications']` query key, so the bell badge updates
 * in lockstep with the list.
 *
 * Filter chips:
 *   - All     → no kind / unread filter (default)
 *   - Unread  → backend `unread=true`
 *   - SLA     → backend `kind=sla.warning` + client-side post-filter for the
 *               sla.* union (the backend `kind` query param accepts a single
 *               value; we widen client-side so both sla.warning and sla.breach
 *               render under the chip)
 *   - Package → backend `kind=package.threshold_crossed` + client-side post-filter
 *               for the package.* union
 *
 * XSS mitigation (T-41-25): payload.message is rendered as a text node via JSX
 * (React default escaping); guarded with `typeof === 'string'`. No
 * dangerouslySetInnerHTML.
 *
 * D-Copy gate: English-only copy ("Notifications", "Mark all read", "All",
 * "Unread", "SLA", "Package", "Loading…", "No notifications", kind labels).
 */
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { CheckIcon } from '@heroicons/react/24/outline';
import { TopBar } from '../../components/ui/TopBar';
import { Card } from '../../components/ui/Card';
import { Btn } from '../../components/ui/Btn';
import { Pill } from '../../components/ui/Pill';
import {
  useNotifications,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
} from '../../hooks/useNotifications';
import type { Notification, NotificationListParams } from '../../services/api/notifications';
import { formatDistanceToNow } from 'date-fns';
import {
  KIND_LABEL,
  KIND_TONE,
  describePayload,
  timesheetReportHref,
} from '../../utils/notificationDisplay';

type Status = 'open' | 'solved';
type KindFilter = 'all' | 'sla' | 'package';

const STATUS_CHIPS: Array<{ id: Status; label: string }> = [
  { id: 'open', label: 'Open' },
  { id: 'solved', label: 'Solved' },
];

const KIND_OPTIONS: Array<{ id: KindFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'sla', label: 'SLA' },
  { id: 'package', label: 'Hour Package' },
];

function paramsFor(status: Status, kind: KindFilter): NotificationListParams {
  const base: NotificationListParams = {
    limit: 100,
    unread: status === 'open' ? true : false,
  };
  if (kind === 'sla') return { ...base, kind: 'sla.warning' };
  if (kind === 'package') return { ...base, kind: 'package.threshold_crossed' };
  return base;
}

export function NotificationsPage() {
  const [status, setStatus] = useState<Status>('open');
  const [kind, setKind] = useState<KindFilter>('all');
  const { data, isLoading } = useNotifications(paramsFor(status, kind));
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const unread = data?.unreadCount ?? 0;

  // For the SLA / Package kind dropdown values we send a single `kind` to the
  // backend (the only value the route schema accepts) AND post-filter the
  // response so the union of related kinds shows up under one option.
  const items = useMemo<Notification[]>(() => {
    const raw = data?.data ?? [];
    if (kind === 'sla') return raw.filter((n) => n.kind.startsWith('sla.'));
    if (kind === 'package') return raw.filter((n) => n.kind.startsWith('package.'));
    return raw;
  }, [data, kind]);

  const breadcrumbs = [{ label: 'Notifications' }];

  return (
    <>
      <TopBar
        title="Notifications"
        breadcrumbs={breadcrumbs}
        actions={
          unread > 0 ? (
            <Btn size="sm" variant="outline-accent" onClick={() => markAll.mutate()}>
              Mark all read
            </Btn>
          ) : undefined
        }
      />
      <div className="p-6 max-w-3xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div className="flex gap-2">
            {STATUS_CHIPS.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStatus(s.id)}
                className={`px-3 py-1.5 rounded-md text-[12px] font-medium ${
                  status === s.id
                    ? // 41-14 Task 0b — orange-dark for 5.65:1 contrast on white text
                      'bg-ms-orange-dark text-white'
                    : 'bg-ms-surface border border-ms-border text-ms-text-secondary hover:bg-ms-surface-2'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 text-[12px] text-ms-text-secondary">
            <span>Kind:</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value as KindFilter)}
              className="px-2 py-1 rounded-md border border-ms-border bg-white text-[12px] text-ms-text-strong focus:outline-none focus:ring-2 focus:ring-ms-orange"
            >
              {KIND_OPTIONS.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <Card>
          {isLoading ? (
            <div className="py-12 text-center text-ms-text-muted">Loading…</div>
          ) : items.length === 0 ? (
            <div className="py-12 text-center text-ms-text-muted">No notifications</div>
          ) : (
            <ul className="divide-y divide-ms-border-subtle -mx-4">
              {items.map((n) => {
                const tone = KIND_TONE[n.kind] ?? { bg: '#F4F1EC', fg: '#5C5C68' };
                const kindLabel = KIND_LABEL[n.kind] ?? n.kind;
                const { projectName, projectId, taskId, detail, href } = describePayload(n.payload);
                // MKS-144 UAT — task.* rows link to the full-page task view,
                // not the project; the timesheet link makes no sense there.
                // `href` is the fallback for projectless kinds (bot usage).
                const titleHref =
                  taskId != null
                    ? `/tasks/${taskId}`
                    : projectId != null
                      ? `/projects/${projectId}`
                      : (href ?? null);
                return (
                  <li
                    key={n.id}
                    className={`px-4 py-3 ${n.readAt ? '' : 'bg-ms-orange/5'}`}
                  >
                    <div className="mb-2">
                      <Pill bg={tone.bg} fg={tone.fg}>
                        {kindLabel}
                      </Pill>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="min-w-0 flex-1">
                        {titleHref != null ? (
                          <Link
                            to={titleHref}
                            className="block text-[14px] font-semibold text-ms-text-strong hover:underline truncate"
                          >
                            {projectName}
                            {detail ? (
                              <span className="ml-2 text-[12px] font-normal text-ms-text-secondary">
                                · {detail}
                              </span>
                            ) : null}
                          </Link>
                        ) : (
                          <div className="text-[14px] font-semibold text-ms-text-strong truncate">
                            {projectName}
                            {detail ? (
                              <span className="ml-2 text-[12px] font-normal text-ms-text-secondary">
                                · {detail}
                              </span>
                            ) : null}
                          </div>
                        )}
                        <div className="text-[11px] text-ms-text-muted mt-0.5">
                          {formatDistanceToNow(new Date(n.createdAt), { addSuffix: true })}
                        </div>
                      </div>
                      {projectId == null && href != null && (
                        <div className="flex items-center gap-3 text-[12.5px] shrink-0">
                          <Link
                            to={href}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            View usage
                          </Link>
                        </div>
                      )}
                      {projectId != null && (
                        <div className="flex items-center gap-3 text-[12.5px] shrink-0">
                          {taskId != null && (
                            <>
                              <Link
                                to={`/tasks/${taskId}`}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                View task
                              </Link>
                              <span className="text-ms-border">·</span>
                            </>
                          )}
                          <Link
                            to={`/projects/${projectId}`}
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            Open project
                          </Link>
                          {taskId == null && (
                            <>
                              <span className="text-ms-border">·</span>
                              <Link
                                to={timesheetReportHref(projectId)}
                                className="text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                View timesheet
                              </Link>
                            </>
                          )}
                        </div>
                      )}
                      <div className="shrink-0">
                        {n.readAt ? (
                          <span
                            className="inline-flex items-center justify-center min-w-[80px] h-9 px-3 rounded-md bg-emerald-100 text-emerald-700 font-semibold gap-1"
                            title="Solved"
                            aria-label="Solved"
                          >
                            <CheckIcon className="w-4 h-4" />
                            <span className="text-[12.5px]">Solved</span>
                          </span>
                        ) : (
                          <button
                            type="button"
                            onClick={() => markRead.mutate(n.id)}
                            disabled={markRead.isPending}
                            title="Mark as solved"
                            aria-label="Mark as solved"
                            className="inline-flex items-center justify-center min-w-[80px] h-9 px-4 rounded-md bg-emerald-600 text-white font-semibold text-[13px] hover:bg-emerald-700 disabled:opacity-50"
                          >
                            Solve
                          </button>
                        )}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}
